// src/components/TaskDetailContent.jsx
//
// Everything known about one task, laid out for reading.
//
// It lived inside DevelopmentTask.jsx, which is where it was first needed and
// the only place that could show it. The Team Pending screen wants the same
// view of the same rows — a backlog you are deciding what to do about is
// exactly where "what IS this task" gets asked — so it moved out here rather
// than being written a second time and drifting.
//
// It renders whatever the row it is handed happens to carry, and prints a dash
// for the rest. The two screens fetch from different procedures, so a field
// one list returns and the other does not is a normal state, not a fault.
//
// The timeline is passed in rather than fetched here: both callers already own
// a dispatch and a loading state, and a component that fetches on mount would
// fire again every time the dialog is reopened.
import React from "react";

import TeamBadge from "./TeamBadge";
import TaskImages from "./TaskImages";
import TaskTimeline from "./TaskTimeline";
import TaskTimeByPerson from "./TaskTimeByPerson";
import "../assets/css/taskTimeByPerson.css";
import { displayStatus, formatDateTimeDisplay } from "../utils/common";

const TaskDetailContent = ({ task, timeline, timelineStatus }) => {
  if (!task) return null;

  const DetailRow = ({
    label,
    value,
    fullWidth = false,
    isBadge = false,
    badgeColor = "secondary",
  }) => (
    <div className={`mb-3 ${fullWidth ? "col-12" : "col-md-4 col-sm-6"}`}>
      <div
        className="text-muted small text-uppercase fw-bold mb-1"
        style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      {isBadge ? (
        <span
          className={`badge bg-${badgeColor} text-${badgeColor === "warning" || badgeColor === "info" ? "dark" : "light"}`}
        >
          {value}
        </span>
      ) : (
        <div
          className="fw-medium text-dark"
          style={{ fontSize: "0.9rem", wordBreak: "break-word" }}
        >
          {value || "-"}
        </div>
      )}
    </div>
  );

  return (
    <div className="p-4" style={{ maxHeight: "80vh", overflowY: "auto" }}>
      <div className="d-flex justify-content-between align-items-start mb-3 pb-3 border-bottom">
        <div>
          <h5 className="fw-bold text-primary mb-1">{task.TaskName}</h5>
          <div className="text-muted small">{task.NatureofQuery}</div>
        </div>
        <div className="d-flex flex-column align-items-end gap-1">
          <span className="badge bg-light text-secondary border">
            Query #{task.QueryNo}
          </span>
          <small className="text-muted">
            {formatDateTimeDisplay(task.QueryDate)}
          </small>
        </div>
      </div>
      <div className="row g-3">
        <div className="col-12">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Client Information
          </h6>
        </div>
        <DetailRow
          label="Client / Company"
          value={task.ClientName || task.CompanyName}
          fullWidth
        />
        <DetailRow label="Project Name" value={task.ProjectName} />
        <DetailRow label="User Name" value={task.Client_UserName} />
        <DetailRow label="Mobile No" value={task.MobileNo} />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Task Settings
          </h6>
        </div>
        <DetailRow
          label="Status"
          value={displayStatus(task.Status)}
          isBadge
          badgeColor={
            task.Status === "Complete" || task.Status === "Testing"
              ? "success"
              : task.Status === "In Progress"
                ? "primary"
                : "secondary"
          }
        />
        <DetailRow
          label="Priority"
          value={task.Priority}
          isBadge
          badgeColor={task.Priority === "High" ? "danger" : "info"}
        />
        <DetailRow
          label="Manual?"
          value={task.isManuallyRaised ? "Yes" : "No"}
          isBadge
          badgeColor={
            task.isManuallyRaised ? "warning" : "dark  border"
          }
        />
        <DetailRow
          label="Assigned Time"
          value={
            formatDateTimeDisplay(task.AssignedTime)
          }
        />
        <DetailRow
          label="Team"
          value={<TeamBadge name={task.TeamName} short={task.TeamShortName} />}
        />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Timing
          </h6>
        </div>
        <DetailRow
          label="Est. Time"
          value={task.EstimateTime ? `${task.EstimateTime} mins` : "-"}
        />
        <DetailRow
          label="Start Time"
          value={
            formatDateTimeDisplay(task.StartTime)
          }
        />
        <DetailRow
          label="End Time"
          value={
            formatDateTimeDisplay(task.EndTime)
          }
        />
        <DetailRow
          label="Time Taken"
          value={task.TotalTimeTaken || task.TimeTaken || "-"}
        />

        {/* The same total, split between the people who produced it.
            A task that passes through three people reports one figure, and
            "Time Taken" above cannot say which part of it was whose — which is
            the question asked of a task that has been handed on twice. */}
        {task.QueryCode && (
          <div className="col-12 mt-2">
            <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded mb-2">
              Time by person
            </h6>
            <TaskTimeByPerson queryCode={task.QueryCode} />
          </div>
        )}

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Menu Path
          </h6>
        </div>
        <DetailRow
          label="Main Menu"
          value={task.MainMenuName || task.FormName}
        />
        <DetailRow label="Sub Menu" value={task.SubMenuName} />
        <DetailRow label="Sub Menu 2" value={task.SubMenuName2} />
        <DetailRow label="Sub Menu 3" value={task.SubMenuName3} />
        <DetailRow label="Sub Menu 4" value={task.SubMenuName4} />

        <div className="col-12 mt-2">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded">
            Content
          </h6>
        </div>
        <DetailRow label="Description" value={task.Description} fullWidth />
        <DetailRow label="Solution" value={task.Solution} fullWidth />

        {/* The screenshots sit with the description, because they are the
            half of "what is wrong" that words are bad at. Read-only: this is
            the detail view, and the place to change them is the task form. */}
        {task.QueryCode && (
          <div className="col-12 mt-2">
            <TaskImages queryCode={task.QueryCode} readOnly />
          </div>
        )}

        {/* --- HISTORY ---
            Hand-overs and status changes on one line. The old view showed
            status changes only, which left the most-asked question — who sent
            this back to us, and why — unanswerable from the screen. */}
        <div className="col-12 mt-4">
          <h6 className="text-primary fw-bold small text-uppercase bg-light p-2 rounded mb-3">
            Task History Timeline
          </h6>
          <TaskTimeline events={timeline} status={timelineStatus} />
        </div>
      </div>
    </div>
  );
};

export default TaskDetailContent;
